import { GraduationCap } from "lucide-react";
import { useNavigate } from "react-router-dom";

import SummaryCard, { joinMeta } from "@/app/components/ResumeBuilder/SummaryCard";
import dropDownOptions from "@/app/seed/dropDownOptions";
import type { EducationItem } from "@/app/types/resume";

/**
 * The level as the reader picked it, not as the server stores it.
 *
 * `educationLevel` holds enum members like "MASTERS" and "POST GRADUATE
 * DIPLOMA", and this card printed them verbatim — so a saved entry came back
 * shouting "MASTERS, MBA, Marketing Management". The dropdown already carries
 * the right label for every value, so reuse it rather than keep a second list.
 */
const EDUCATION_LEVEL_LABELS: Record<string, string> = Object.fromEntries(
	(dropDownOptions.educationLevels as { name: string; value: string }[])
		.filter((option) => option.value)
		.map((option) => [option.value, option.name]),
);

const MONTH_ABBREVIATIONS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * "Nov 2020" from the two separate graduation fields, matching how the work
 * history card prints its dates.
 *
 * `gMonth` was indexed into a month-name array with no bounds check, so a saved
 * entry missing the month read "undefined 2020". A year on its own is a real
 * answer here — some readers only record one — so fall back to it.
 */
function formatGraduation(gMonth: string | number, gYear: string | number): string {
	const month = Number(gMonth);
	const year = String(gYear ?? "").trim();

	if (!year) return "";
	if (!Number.isInteger(month) || month < 1 || month > 12) return year;

	return `${MONTH_ABBREVIATIONS[month - 1]} ${year}`;
}

interface EducationSummaryBoxProps {
	item: EducationItem;
	index: number | string;
	deleteEduSummary: (referenceId: string | undefined) => void;
}

const EducationSummaryBox = ({ item, index, deleteEduSummary }: EducationSummaryBoxProps) => {
	const navigate = useNavigate();

	const eduInfoHandler = () => {
		navigate("/resume/education-information", {
			state: {
				referenceId: index,
			},
		});
	};

	const levelLabel = EDUCATION_LEVEL_LABELS[item.educationLevel] ?? item.educationLevel;
	const fieldOfStudy = item.fieldOfStudy?.trim();

	// The heading used to be "level, field of study", which reads as a stutter for
	// the common case — "Bachelor, Bachelor in Engineering". Lead with the specific
	// answer and demote the level to a badge; if the reader gave no field of study,
	// the level is all there is, so it becomes the heading and the badge goes away.
	const title = fieldOfStudy || levelLabel;
	const badge = fieldOfStudy && levelLabel !== fieldOfStudy ? levelLabel : undefined;

	// `schoolCity` is the pre-joined string from the fetch transform; it is absent
	// on an entry the reader has only just typed, so rebuild it from the parts.
	const location = item.schoolCity?.trim() || joinMeta([item.city, item.region, item.country]);

	return (
		<SummaryCard
			icon={<GraduationCap className="size-5" />}
			title={title}
			badge={badge}
			subtitle={item.schoolName}
			meta={[location, formatGraduation(item.gMonth, item.gYear)]}
			html={item.description}
			onEdit={eduInfoHandler}
			onDelete={() => deleteEduSummary(item.referenceId)}
			deleteTitle="Delete this education entry?"
			deleteDescription={`"${title}" will be removed from your resume. You cannot undo this.`}
			entryLabel={title}
		/>
	);
};

export default EducationSummaryBox;
